import { useEffect, useState, useCallback } from 'react';
import { supabase } from './supabaseClient';

const GOLD = '#C9A84C';
const GOLD_LIGHT = '#E8C96A';
const GOLD_PALE = '#F9F1DC';
const GOLD_BORDER = 'rgba(201,168,76,0.2)';
const CREAM = '#FDFBF7';
const CHARCOAL = '#1C1C1E';
const MUTED = '#8A8070';
const WHITE = '#FFFFFF';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Jost:wght@300;400;500;600&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Jost', sans-serif; background: ${CREAM}; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-thumb { background: ${GOLD_BORDER}; border-radius: 4px; }

  .score-input {
    width: 100%;
    padding: 18px;
    font-size: 32px;
    font-family: 'Cormorant Garamond', serif;
    font-weight: 600;
    text-align: center;
    border: 2px solid ${GOLD_BORDER};
    border-radius: 14px;
    background: ${WHITE};
    color: ${CHARCOAL};
    outline: none;
    transition: all 0.2s;
    -moz-appearance: textfield;
  }
  .score-input::-webkit-outer-spin-button,
  .score-input::-webkit-inner-spin-button { -webkit-appearance: none; }
  .score-input:focus {
    border-color: ${GOLD};
    box-shadow: 0 0 0 4px rgba(201,168,76,0.12);
  }
  .score-input.valid { border-color: #43A047; }
  .score-input.invalid { border-color: #E53E3E; }

  .feedback-input {
    width: 100%;
    padding: 14px 16px;
    font-size: 14px;
    font-family: 'Jost', sans-serif;
    border: 1px solid ${GOLD_BORDER};
    border-radius: 12px;
    background: ${WHITE};
    color: ${CHARCOAL};
    outline: none;
    resize: none;
    transition: border 0.2s;
    line-height: 1.6;
  }
  .feedback-input:focus {
    border-color: ${GOLD};
    box-shadow: 0 0 0 3px rgba(201,168,76,0.1);
  }
  .feedback-input::placeholder { color: #C4B8A8; }

  .submit-btn {
    width: 100%;
    padding: 16px;
    background: linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT});
    border: none;
    border-radius: 12px;
    font-family: 'Jost', sans-serif;
    font-size: 15px;
    font-weight: 600;
    color: ${WHITE};
    letter-spacing: 0.08em;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 4px 16px rgba(201,168,76,0.3);
  }
  .submit-btn:hover:not(:disabled) { opacity: 0.92; transform: translateY(-1px); box-shadow: 0 6px 20px rgba(201,168,76,0.4); }
  .submit-btn:active:not(:disabled) { transform: translateY(0); }
  .submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  .nav-btn {
    flex: 1;
    padding: 12px;
    background: ${WHITE};
    border: 1px solid ${GOLD_BORDER};
    border-radius: 10px;
    font-family: 'Jost', sans-serif;
    font-size: 13px;
    color: ${CHARCOAL};
    cursor: pointer;
    transition: all 0.15s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }
  .nav-btn:hover:not(:disabled) { background: ${GOLD_PALE}; border-color: ${GOLD}; color: ${GOLD}; }
  .nav-btn:disabled { opacity: 0.3; cursor: not-allowed; }

  .contestant-card {
    transition: all 0.2s;
  }
  .contestant-card:hover { transform: translateY(-1px); }

  .toast {
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    background: ${CHARCOAL};
    color: ${WHITE};
    padding: 12px 24px;
    border-radius: 100px;
    font-size: 13px;
    font-family: 'Jost', sans-serif;
    letter-spacing: 0.04em;
    z-index: 999;
    box-shadow: 0 8px 24px rgba(0,0,0,0.2);
    animation: slideUp 0.3s ease;
  }
  @keyframes slideUp {
    from { opacity: 0; transform: translateX(-50%) translateY(10px); }
    to { opacity: 1; transform: translateX(-50%) translateY(0); }
  }
`;

// ─── Toast notification ───────────────────────────────────────────────────────
function Toast({ message }) {
  return <div className="toast">✓ {message}</div>;
}

// ─── Progress Bar ─────────────────────────────────────────────────────────────
function ProgressBar({ current, total, label }) {
  const pct = total > 0 ? ((current) / total) * 100 : 0;
  return (
    <div style={{ marginBottom: '6px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
        <span style={{ fontSize: '11px', color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{label}</span>
        <span style={{ fontSize: '11px', color: GOLD, fontWeight: '600' }}>{current} / {total}</span>
      </div>
      <div style={{ height: '4px', background: '#F0EBE0', borderRadius: '2px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT})`, borderRadius: '2px', transition: 'width 0.4s ease' }} />
      </div>
    </div>
  );
}

// ─── Contestant Avatar ────────────────────────────────────────────────────────
function ContestantAvatar({ contestant, size = 80 }) {
  const name = contestant?.users?.full_name || 'Contestant';
  const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div style={{
      width: size, height: size,
      borderRadius: '50%',
      background: `linear-gradient(135deg, ${GOLD}33, ${GOLD}11)`,
      border: `2px solid ${GOLD_BORDER}`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Cormorant Garamond', serif",
      fontSize: size * 0.3,
      fontWeight: '600',
      color: GOLD,
      flexShrink: 0,
      overflow: 'hidden',
    }}>
      {contestant?.headshot_url ? (
        <img src={contestant.headshot_url} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : initials}
    </div>
  );
}

// ─── Scoring Screen ───────────────────────────────────────────────────────────
function ScoringScreen({ judge, pageant, onComplete, onSignOut }) {
  const [categories, setCategories] = useState([]);
  const [contestants, setContestants] = useState([]);
  const [categoryIndex, setCategoryIndex] = useState(0);
  const [contestantIndex, setContestantIndex] = useState(0);
  const [scores, setScores] = useState({}); // key: `${categoryId}-${contestantId}` → { score, feedback, submitted }
  const [currentScore, setCurrentScore] = useState('');
  const [currentFeedback, setCurrentFeedback] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState('');
  const [view, setView] = useState('scoring'); // 'scoring' | 'overview' | 'complete'
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchData(); }, []);

  async function fetchData() {
    setLoading(true);
    const [catRes, conRes] = await Promise.all([
      supabase.from('categories').select('*').eq('pageant_id', pageant.id).order('display_order'),
      supabase.from('contestant_profiles')
        .select('*, users(full_name, email, headshot_url)')
        .eq('pageant_id', pageant.id)
        .eq('is_eligible', true)
        .order('contestant_number'),
    ]);

    const cats = catRes.data || [];
    const cons = conRes.data || [];
    setCategories(cats);
    setContestants(cons);

    // Load existing scores for this judge
    if (cons.length > 0) {
      const { data: existingScores } = await supabase
        .from('scores')
        .select('*')
        .eq('judge_id', judge.id)
        .in('contestant_profile_id', cons.map(c => c.id));

      const scoreMap = {};
      (existingScores || []).forEach(s => {
        scoreMap[`${s.category_id}-${s.contestant_profile_id}`] = {
          score: s.raw_score,
          feedback: s.feedback_text || '',
          submitted: true,
          scoreId: s.id,
        };
      });
      setScores(scoreMap);
    }
    setLoading(false);
  }

  const currentCategory = categories[categoryIndex];
  const currentContestant = contestants[contestantIndex];
  const scoreKey = currentCategory && currentContestant ? `${currentCategory.id}-${currentContestant.id}` : null;
  const existingEntry = scoreKey ? scores[scoreKey] : null;

  // Load existing score when navigating
  useEffect(() => {
    if (existingEntry) {
      setCurrentScore(existingEntry.score?.toString() || '');
      setCurrentFeedback(existingEntry.feedback || '');
    } else {
      setCurrentScore('');
      setCurrentFeedback('');
    }
  }, [categoryIndex, contestantIndex]);

  const scoreNum = parseFloat(currentScore);
  const isValid = currentScore !== '' && !isNaN(scoreNum) && currentCategory &&
    scoreNum >= currentCategory.min_score && scoreNum <= currentCategory.max_score;

  const totalScores = categories.length * contestants.length;
  const submittedCount = Object.values(scores).filter(s => s.submitted).length;

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  }

  async function handleSubmit() {
    if (!isValid || !currentCategory || !currentContestant) return;
    setSubmitting(true);

    const { error } = await supabase.from('scores').insert({
      judge_id: judge.id,
      contestant_profile_id: currentContestant.id,
      category_id: currentCategory.id,
      raw_score: scoreNum,
      feedback_text: currentFeedback.trim() || null,
      device_id: navigator.userAgent.slice(0, 50),
    });

    if (!error) {
      setScores(prev => ({
        ...prev,
        [scoreKey]: { score: scoreNum, feedback: currentFeedback, submitted: true },
      }));
      showToast('Score saved!');

      // Auto-advance to next contestant
      if (contestantIndex < contestants.length - 1) {
        setContestantIndex(i => i + 1);
      } else if (categoryIndex < categories.length - 1) {
        // Move to next category
        setCategoryIndex(i => i + 1);
        setContestantIndex(0);
        showToast(`Category complete! Moving to next category.`);
      } else {
        setView('complete');
      }
    } else {
      showToast('Error saving score. Try again.');
    }
    setSubmitting(false);
  }

  // Category submitted count
  function categoryProgress(catId) {
    return contestants.filter(c => scores[`${catId}-${c.id}`]?.submitted).length;
  }

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: CREAM, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>♛</div>
          <p style={{ color: GOLD, fontSize: '13px', letterSpacing: '0.1em' }}>LOADING SCORING...</p>
        </div>
      </div>
    );
  }

  if (contestants.length === 0 || categories.length === 0) {
    return (
      <div style={{ minHeight: '100vh', background: CREAM, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>⚖️</div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '24px', color: CHARCOAL, marginBottom: '8px' }}>Not Ready Yet</h2>
          <p style={{ color: MUTED, fontSize: '14px' }}>This pageant has no contestants or categories set up yet. Contact your director.</p>
        </div>
      </div>
    );
  }

  // ── Complete Screen ──
  if (view === 'complete') {
    return (
      <>
        <style>{styles}</style>
        <div style={{ minHeight: '100vh', background: CREAM, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ textAlign: 'center', maxWidth: '360px' }}>
            <div style={{ width: '80px', height: '80px', background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '36px', margin: '0 auto 24px', boxShadow: `0 8px 32px rgba(201,168,76,0.4)` }}>✓</div>
            <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '32px', color: CHARCOAL, marginBottom: '8px' }}>Scoring Complete</h1>
            <p style={{ color: MUTED, fontSize: '14px', marginBottom: '8px' }}>{pageant.name}</p>
            <p style={{ color: GOLD, fontSize: '13px', marginBottom: '32px' }}>{submittedCount} scores submitted</p>
            <button onClick={() => setView('overview')} style={{ background: 'none', border: `1px solid ${GOLD_BORDER}`, borderRadius: '10px', padding: '12px 24px', color: GOLD, fontFamily: "'Jost', sans-serif", fontSize: '13px', cursor: 'pointer' }}>Review My Scores</button>
          </div>
        </div>
      </>
    );
  }

  // ── Overview Screen ──
  if (view === 'overview') {
    return (
      <>
        <style>{styles}</style>
        <div style={{ minHeight: '100vh', background: CREAM }}>
          <div style={{ background: WHITE, borderBottom: `1px solid ${GOLD_BORDER}`, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px', position: 'sticky', top: 0, zIndex: 10 }}>
            <button onClick={() => setView('scoring')} style={{ background: 'none', border: 'none', color: GOLD, fontSize: '20px', cursor: 'pointer', lineHeight: 1 }}>←</button>
            <div>
              <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', color: CHARCOAL }}>Score Overview</h1>
              <p style={{ fontSize: '11px', color: MUTED }}>{submittedCount} of {totalScores} submitted</p>
            </div>
          </div>
          <div style={{ padding: '20px' }}>
            {categories.map(cat => (
              <div key={cat.id} style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', color: CHARCOAL }}>{cat.name}</h3>
                  <span style={{ fontSize: '11px', color: GOLD }}>{categoryProgress(cat.id)}/{contestants.length}</span>
                </div>
                <div style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '12px', overflow: 'hidden' }}>
                  {contestants.map((c, i) => {
                    const entry = scores[`${cat.id}-${c.id}`];
                    return (
                      <div key={c.id} style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderTop: i > 0 ? `1px solid ${GOLD_BORDER}` : 'none', gap: '10px' }}
                        onClick={() => { setCategoryIndex(categories.indexOf(cat)); setContestantIndex(i); setView('scoring'); }}>
                        <ContestantAvatar contestant={c} size={36} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: '500', color: CHARCOAL }}>{c.users?.full_name}</div>
                          <div style={{ fontSize: '11px', color: MUTED }}>#{c.contestant_number}</div>
                        </div>
                        {entry?.submitted ? (
                          <span style={{ fontSize: '18px', fontFamily: "'Cormorant Garamond', serif", fontWeight: '700', color: GOLD }}>{entry.score}</span>
                        ) : (
                          <span style={{ fontSize: '11px', color: '#E53E3E', background: '#FFF0F0', borderRadius: '20px', padding: '3px 8px' }}>Missing</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  // ── Main Scoring View ──
  return (
    <>
      <style>{styles}</style>
      <div style={{ minHeight: '100vh', background: CREAM, maxWidth: '480px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ background: WHITE, borderBottom: `1px solid ${GOLD_BORDER}`, padding: '14px 20px', position: 'sticky', top: 0, zIndex: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ width: '28px', height: '28px', background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`, borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px' }}>♛</div>
              <div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '15px', fontWeight: '600', color: CHARCOAL, lineHeight: 1 }}>Scorenity</div>
                <div style={{ fontSize: '9px', color: MUTED, letterSpacing: '0.1em' }}>JUDGE PORTAL</div>
              </div>
            </div>
            <button onClick={() => setView('overview')} style={{ background: GOLD_PALE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '8px', padding: '6px 12px', fontSize: '11px', color: GOLD, cursor: 'pointer', fontFamily: "'Jost', sans-serif" }}>
              Overview
            </button>
          </div>
          <ProgressBar current={submittedCount} total={totalScores} label="Overall Progress" />
        </div>

        {/* Category selector */}
        <div style={{ padding: '16px 20px 0', overflowX: 'auto' }}>
          <div style={{ display: 'flex', gap: '8px', paddingBottom: '4px' }}>
            {categories.map((cat, i) => {
              const done = categoryProgress(cat.id) === contestants.length;
              const active = i === categoryIndex;
              return (
                <button key={cat.id} onClick={() => { setCategoryIndex(i); setContestantIndex(0); }} style={{
                  flexShrink: 0,
                  padding: '7px 14px',
                  borderRadius: '20px',
                  border: active ? 'none' : `1px solid ${GOLD_BORDER}`,
                  background: active ? `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})` : done ? GOLD_PALE : WHITE,
                  color: active ? WHITE : done ? GOLD : CHARCOAL,
                  fontSize: '12px',
                  fontFamily: "'Jost', sans-serif",
                  fontWeight: active ? '600' : '400',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center', gap: '5px',
                }}>
                  {done && !active && <span style={{ fontSize: '10px' }}>✓</span>}
                  {cat.name}
                  <span style={{ fontSize: '10px', opacity: 0.7 }}>{cat.weight_percent}%</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main scoring card */}
        <div style={{ padding: '16px 20px' }}>

          {/* Category info */}
          <div style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '14px', padding: '16px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', color: CHARCOAL }}>{currentCategory?.name}</h2>
                <p style={{ fontSize: '11px', color: MUTED, marginTop: '2px' }}>Score {currentCategory?.min_score} – {currentCategory?.max_score} · Weight: {currentCategory?.weight_percent}%</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '11px', color: MUTED }}>Contestant</div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', color: GOLD, fontWeight: '600', lineHeight: 1 }}>
                  {contestantIndex + 1}<span style={{ fontSize: '14px', color: MUTED }}>/{contestants.length}</span>
                </div>
              </div>
            </div>
            <div style={{ marginTop: '10px' }}>
              <ProgressBar current={categoryProgress(currentCategory?.id)} total={contestants.length} label={`${currentCategory?.name} progress`} />
            </div>
          </div>

          {/* Contestant card */}
          <div className="contestant-card" style={{ background: WHITE, border: `2px solid ${existingEntry?.submitted ? '#C6F6D5' : GOLD_BORDER}`, borderRadius: '16px', padding: '20px', marginBottom: '16px', position: 'relative', overflow: 'hidden' }}>
            {existingEntry?.submitted && (
              <div style={{ position: 'absolute', top: '12px', right: '12px', background: '#E8F5E9', color: '#2E7D32', fontSize: '10px', borderRadius: '20px', padding: '3px 8px', fontWeight: '600' }}>✓ Scored</div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
              <ContestantAvatar contestant={currentContestant} size={72} />
              <div>
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', color: CHARCOAL, lineHeight: 1, marginBottom: '4px' }}>
                  {currentContestant?.users?.full_name}
                </h3>
                {currentContestant?.local_title && (
                  <p style={{ fontSize: '12px', color: GOLD, marginBottom: '2px' }}>{currentContestant.local_title}</p>
                )}
                <p style={{ fontSize: '12px', color: MUTED }}>
                  #{currentContestant?.contestant_number || contestantIndex + 1}
                </p>
                {currentContestant?.platform && (
                  <p style={{ fontSize: '11px', color: MUTED, marginTop: '4px', fontStyle: 'italic' }}>"{currentContestant.platform}"</p>
                )}
              </div>
            </div>

            {/* Score input */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '11px', color: MUTED, letterSpacing: '0.12em', textTransform: 'uppercase', display: 'block', marginBottom: '8px', textAlign: 'center' }}>
                Enter Score ({currentCategory?.min_score} – {currentCategory?.max_score})
              </label>
              <input
                className={`score-input ${currentScore === '' ? '' : isValid ? 'valid' : 'invalid'}`}
                type="number"
                min={currentCategory?.min_score}
                max={currentCategory?.max_score}
                step="0.1"
                placeholder={`${currentCategory?.min_score} – ${currentCategory?.max_score}`}
                value={currentScore}
                onChange={e => setCurrentScore(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && isValid && handleSubmit()}
              />
              {currentScore !== '' && !isValid && (
                <p style={{ fontSize: '12px', color: '#E53E3E', textAlign: 'center', marginTop: '4px' }}>
                  Score must be between {currentCategory?.min_score} and {currentCategory?.max_score}
                </p>
              )}
            </div>

            {/* Feedback */}
            <div>
              <label style={{ fontSize: '11px', color: MUTED, letterSpacing: '0.12em', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>
                Feedback <span style={{ textTransform: 'none', color: '#C4B8A8', letterSpacing: 0 }}>(shown anonymously to contestant)</span>
              </label>
              <textarea
                className="feedback-input"
                rows={3}
                placeholder="Optional constructive feedback for this contestant..."
                value={currentFeedback}
                onChange={e => setCurrentFeedback(e.target.value)}
              />
            </div>
          </div>

          {/* Submit button */}
          <button className="submit-btn" onClick={handleSubmit} disabled={!isValid || submitting}>
            {submitting ? 'Saving...' : existingEntry?.submitted ? 'Update Score & Continue →' : 'Submit Score & Continue →'}
          </button>

          {/* Nav buttons */}
          <div style={{ display: 'flex', gap: '10px', marginTop: '12px' }}>
            <button className="nav-btn" onClick={() => {
              if (contestantIndex > 0) setContestantIndex(i => i - 1);
              else if (categoryIndex > 0) { setCategoryIndex(i => i - 1); setContestantIndex(contestants.length - 1); }
            }} disabled={categoryIndex === 0 && contestantIndex === 0}>
              ← Previous
            </button>
            <button className="nav-btn" onClick={() => {
              if (contestantIndex < contestants.length - 1) setContestantIndex(i => i + 1);
              else if (categoryIndex < categories.length - 1) { setCategoryIndex(i => i + 1); setContestantIndex(0); }
            }} disabled={categoryIndex === categories.length - 1 && contestantIndex === contestants.length - 1}>
              Skip →
            </button>
          </div>

          {/* Missing scores warning */}
          {submittedCount < totalScores && submittedCount > 0 && (
            <div style={{ marginTop: '16px', background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: '10px', padding: '12px 16px', fontSize: '12px', color: '#F57F17', textAlign: 'center' }}>
              {totalScores - submittedCount} score{totalScores - submittedCount !== 1 ? 's' : ''} remaining
            </div>
          )}

          <p style={{ textAlign: 'center', fontSize: '10px', color: MUTED, marginTop: '20px', letterSpacing: '0.06em' }}>
            Scorenity · Scores are saved securely
          </p>
        </div>
      </div>

      {toast && <Toast message={toast} />}
    </>
  );
}

// ─── Pageant Selector ─────────────────────────────────────────────────────────
function PageantSelector({ judge, onSelect, onSignOut }) {
  const [pageants, setPageants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetch() {
      const { data } = await supabase
        .from('judges')
        .select('*, pageants(id, name, event_date, status, org_id, organizations(name))')
        .eq('user_id', judge.id)
        .eq('is_active', true);

      const active = (data || []).filter(j => j.pageants?.status === 'scoring');
      setPageants(active);
      setLoading(false);

      if (active.length === 1) onSelect(active[0].pageants, active[0]);
    }
    fetch();
  }, []);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: CREAM, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>♛</div>
          <p style={{ color: GOLD, fontSize: '13px', letterSpacing: '0.1em' }}>LOADING...</p>
        </div>
      </div>
    );
  }

  if (pageants.length === 0) {
    return (
      <div style={{ minHeight: '100vh', background: CREAM, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ textAlign: 'center', maxWidth: '320px' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>⚖️</div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '24px', color: CHARCOAL, marginBottom: '8px' }}>No Active Scoring</h2>
          <p style={{ color: MUTED, fontSize: '13px', lineHeight: '1.6', marginBottom: '20px' }}>You don't have any pageants open for scoring right now. Check back when your director opens scoring.</p>
          {onSignOut && <button onClick={onSignOut} style={{ background: 'none', border: `1px solid rgba(201,168,76,0.2)`, borderRadius: '8px', padding: '8px 16px', fontSize: '12px', color: MUTED, cursor: 'pointer', fontFamily: "'Jost', sans-serif" }}>Sign Out</button>}
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: CREAM, padding: '40px 20px' }}>
      <div style={{ maxWidth: '420px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ width: '56px', height: '56px', background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`, borderRadius: '14px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', marginBottom: '16px' }}>♛</div>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', color: CHARCOAL }}>Select Pageant</h1>
          <p style={{ fontSize: '13px', color: MUTED, marginTop: '4px' }}>Choose a pageant to begin scoring</p>
        </div>
        {pageants.map(j => (
          <div key={j.id} onClick={() => onSelect(j.pageants, j)} style={{ background: WHITE, border: `1px solid ${GOLD_BORDER}`, borderRadius: '14px', padding: '20px', marginBottom: '12px', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
            onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', color: CHARCOAL, marginBottom: '4px' }}>{j.pageants?.name}</div>
            <div style={{ fontSize: '12px', color: MUTED }}>{j.pageants?.organizations?.name}</div>
            {j.pageants?.event_date && <div style={{ fontSize: '11px', color: GOLD, marginTop: '6px' }}>{new Date(j.pageants.event_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>}
            <div style={{ marginTop: '10px', display: 'inline-flex', alignItems: 'center', gap: '5px', background: '#E8F0FE', color: '#1A56DB', borderRadius: '20px', padding: '4px 10px', fontSize: '11px', fontWeight: '500' }}>
              <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#3B82F6', display: 'inline-block' }} />
              Scoring Open
            </div>
          </div>
        ))}
        {onSignOut && (
          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <button onClick={onSignOut} style={{ background: 'none', border: `1px solid rgba(201,168,76,0.2)`, borderRadius: '8px', padding: '8px 20px', fontSize: '12px', color: MUTED, cursor: 'pointer', fontFamily: "'Jost', sans-serif" }}>Sign Out</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Judge Portal ────────────────────────────────────────────────────────
export default function JudgePortal({ user, onSignOut }) {
  const [judge, setJudge] = useState(null);
  const [selectedPageant, setSelectedPageant] = useState(null);
  const [selectedJudge, setSelectedJudge] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchJudge() {
      const { data } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();
      setJudge(data);
      setLoading(false);
    }
    fetchJudge();
  }, []);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: CREAM, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>♛</div>
          <p style={{ color: GOLD, fontSize: '13px', letterSpacing: '0.1em' }}>LOADING...</p>
        </div>
      </div>
    );
  }

  if (selectedPageant && selectedJudge) {
    return <ScoringScreen judge={selectedJudge} pageant={selectedPageant} onComplete={() => setSelectedPageant(null)} onSignOut={onSignOut} />;
  }

  return <PageantSelector judge={judge} onSelect={(pageant, judgeRow) => { setSelectedPageant(pageant); setSelectedJudge(judgeRow); }} onSignOut={onSignOut} />;
}